import { useState } from "react";
import { Upload, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useSite, asset } from "@/context/SiteContext";
import { resizeImage } from "@/components/ImagePicker";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

const GROUPS = [
  { title: "الشعار والهيدر", images: [["logo", "الشعار"]], fields: [["site_name_1", "اسم الموقع (الجزء الأول)"], ["site_name_2", "اسم الموقع (الجزء الملوّن)"], ["site_name_en", "الاسم بالإنجليزية"], ["topbar_text", "نص الشريط العلوي"]] },
  { title: "الواجهة الرئيسية", images: [["hero_image", "صورة الواجهة"]], fields: [["hero_badge", "الشارة الصغيرة"], ["hero_title", "العنوان (بداية)"], ["hero_highlight", "الكلمة الملوّنة"], ["hero_title_end", "العنوان (نهاية)"], ["hero_subtitle", "الوصف", true], ["sections_title", "عنوان الأقسام"]] },
  ...[["auction", "المزاد العلني"], ["sale", "البيع"], ["rent", "الإيجار"], ["job", "الوظائف"], ["invest", "الاستثمار والشراكة"]].map(([k, l]) => ({
    title: `قسم ${l}`, images: [[`${k}_image`, "صورة القسم"]], fields: [[`${k}_title`, "العنوان"], [`${k}_desc`, "الوصف"]],
  })),
  { title: "التذييل", images: [], fields: [["footer_about", "نبذة عن الموقع", true]] },
];

const ImageField = ({ k, label }) => {
  const site = useSite();
  const upload = async (file) => {
    try { const { data } = await api.post(`/admin/settings/image/${k}`, { data: await resizeImage(file) }); site.setSite(data); toast.success("تم تحديث الصورة"); } catch (e) { toast.error(errMsg(e)); }
  };
  const reset = async () => {
    try { const { data } = await api.delete(`/admin/settings/image/${k}`); site.setSite(data); toast.success("تمت الاستعادة"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-2">
      <div className="text-xs font-semibold">{label}</div>
      <img src={asset(site[k])} alt="" className={`rounded-xl border bg-slate-50 ${k === "logo" ? "h-20 object-contain p-2" : "h-28 w-48 object-cover"}`} data-testid={`site-img-${k}`} />
      <div className="flex gap-2">
        <label className="flex items-center gap-1 text-xs font-bold bg-brand/10 text-brand rounded-lg px-3 py-1.5 cursor-pointer">
          <Upload size={13} />تغيير<input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid={`site-img-input-${k}`} onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
        </label>
        <button onClick={reset} data-testid={`site-img-reset-${k}`} className="text-xs text-slate-500 flex items-center gap-1"><RotateCcw size={12} />الافتراضي</button>
      </div>
    </div>
  );
};

const Colors = ({ f, set }) => (
  <div className="flex flex-wrap gap-6">
    {[["primary_color", "اللون الرئيسي"], ["accent_color", "اللون الثانوي"]].map(([k, l]) => (
      <label key={k} className="flex items-center gap-3">
        <input type="color" value={f[k]} onChange={(e) => set(k, e.target.value)} className="w-12 h-12 rounded-xl border cursor-pointer" data-testid={`site-color-${k}`} />
        <span className="text-sm font-semibold">{l}<span className="block text-xs text-slate-400" dir="ltr">{f[k]}</span></span>
      </label>
    ))}
    <label className="flex items-center gap-2 text-sm font-semibold"><Switch checked={f.topbar_enabled} onCheckedChange={(v) => set("topbar_enabled", v)} data-testid="site-topbar-toggle" />إظهار الشريط العلوي</label>
  </div>
);

export const SiteEditor = () => {
  const site = useSite();
  const [f, setF] = useState(site);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setBusy(true);
    const { setSite, ...rest } = f;
    try { const { data } = await api.put("/admin/settings", rest); site.setSite(data); toast.success("تم حفظ التغييرات"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const resetAll = async () => {
    if (!window.confirm("إرجاع كل إعدادات الواجهة للوضع الافتراضي؟")) return;
    const { data } = await api.post("/admin/settings/reset"); site.setSite(data); setF(data);
  };
  return (
    <div className="space-y-5" data-testid="site-editor">
      <div className="bg-white border rounded-xl p-5"><h3 className="font-bold mb-4">الألوان</h3><Colors f={f} set={set} /></div>
      {GROUPS.map((g) => (
        <div key={g.title} className="bg-white border rounded-xl p-5">
          <h3 className="font-bold mb-4">{g.title}</h3>
          <div className="grid md:grid-cols-[auto_1fr] gap-6">
            {g.images.length > 0 && <div className="space-y-4">{g.images.map(([k, l]) => <ImageField key={k} k={k} label={l} />)}</div>}
            <div className="grid sm:grid-cols-2 gap-3">
              {g.fields.map(([k, l, long]) => (
                <div key={k} className={long ? "sm:col-span-2" : ""}>
                  <label className="text-xs font-semibold">{l}</label>
                  {long ? <Textarea data-testid={`site-field-${k}`} value={f[k]} onChange={(e) => set(k, e.target.value)} className="rounded-xl mt-1" />
                    : <Input data-testid={`site-field-${k}`} value={f[k]} onChange={(e) => set(k, e.target.value)} className="h-10 rounded-xl mt-1" />}
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
      <div className="sticky bottom-4 flex gap-3 bg-white/90 backdrop-blur border rounded-lg p-2 w-fit shadow-lg">
        <Button data-testid="site-save-btn" disabled={busy} onClick={save} className="rounded-lg bg-brand hover:bg-brand-dark px-8">حفظ النصوص والألوان</Button>
        <Button data-testid="site-reset-btn" variant="ghost" onClick={resetAll} className="rounded-lg">استعادة الافتراضي</Button>
      </div>
    </div>
  );
};
